import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSettings } from '../hooks/useSettings';

// Initial customer state (from SalesInvoiceMobile)
const initialCustomer = {
  Phone: '',
  Name: '',
  Company: '',
  GST: '',
  Locality: '',
  State: '',
  Email: '',
  Address: '',
  ReferredBy: '',
  SelectCompany: '',
  Location: '',
  Partner: '',
  InvoiceDate: '',
  customerCode: '',
};

// Create initial state function that uses settings
const createInitialInvoiceState = (salesInvoiceSettings) => ({
  customer: initialCustomer,
  products: [],
  payments: {
    splitPaymentEnabled: false,
    selectedPaymentMethod: 'Cash',
    cashPayment: '',
    walletProvider: 'PhonePe',
    walletAmount: '',
    walletTxNo: '',
    chequeAmount: '',
    chequeNo: '',
    couponNo: '',
    couponAmount: '0',
    couponAmountLocked: false,
    creditNoteNo: '',
    creditNoteAmount: '0',
    pointsRedeem: '',
    eligiblePoints: '',
    bankTransAmount: '',
    otherAmount: '', // For PaidbyOthers (Card payment)
    payBefore: '',
  },
  notes: '',
  repName: '',
  invoiceForm: {
    QuoteNo: '',
    Mode: '',
    VehicleNo: '',
    Delivered: true,
    AddInFIFO: salesInvoiceSettings?.fifoInvoiceOrder || false
  },
  // Shipping form state
  shipToForm: {
    Name: '',
    Company: '',
    Phone: '',
    Email: '',
    GST: '',
    Address: '',
    State: '',
  },
  shipToSame: true,
  // Unknown customer state
  isUnknown: false,
  // Edit mode state
  isEditMode: false,
  editingInvoiceId: null,
  onBackToReports: null,
  onUpdateSuccess: null,
});

const SalesInvoiceContext = createContext(null);

export const SalesInvoiceProvider = ({ children }) => {
  const { salesInvoiceSettings } = useSettings();
  // Use default settings if not loaded yet (when not authenticated)
  const defaultSettings = {
    fifoInvoiceOrder: false,
    invoiceQtyPopupRequired: false,
    invoicePartnerRequired: false,
    imageInInvoice: false,
    splitPayment: false,
    shipAddressNeeded: false,
    showCostPrice: false,
    unknownUserTicketed: false,
    tag1Name: '',
    tag2Name: '',
  };
  const settings = salesInvoiceSettings || defaultSettings;
  const initialInvoiceState = createInitialInvoiceState(settings);
  
  const [customer, setCustomer] = useState(initialInvoiceState.customer);
  const [products, setProducts] = useState(initialInvoiceState.products);
  const [payments, setPayments] = useState(initialInvoiceState.payments);
  const [notes, setNotes] = useState(initialInvoiceState.notes);
  const [repName, setRepName] = useState(initialInvoiceState.repName);
  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceState.invoiceForm);
  const [shipToForm, setShipToForm] = useState(initialInvoiceState.shipToForm);
  const [shipToSame, setShipToSame] = useState(initialInvoiceState.shipToSame);
  const [isUnknown, setIsUnknown] = useState(initialInvoiceState.isUnknown);
  const [isEditMode, setIsEditMode] = useState(initialInvoiceState.isEditMode);
  const [editingInvoiceId, setEditingInvoiceId] = useState(initialInvoiceState.editingInvoiceId);
  const [onBackToReports, setOnBackToReports] = useState(initialInvoiceState.onBackToReports);
  const [onUpdateSuccess, setOnUpdateSuccess] = useState(initialInvoiceState.onUpdateSuccess);

  // Update AddInFIFO when settings change
  useEffect(() => {
    if (settings?.fifoInvoiceOrder !== undefined) {
      const newAddInFIFO = settings.fifoInvoiceOrder;
      setInvoiceForm(prev => {
        // Only update if value is different to avoid unnecessary re-renders
        if (prev.AddInFIFO !== newAddInFIFO) {
          return { ...prev, AddInFIFO: newAddInFIFO };
        }
        return prev;
      });
    }
  }, [settings?.fifoInvoiceOrder]);

  // Reset form for new invoice - clears customer, GST, QuoteNo, products, etc.
  const resetForNewInvoice = () => {
    const keepUnknown = settings?.unknownUserTicketed ?? false;
    setCustomer({ ...initialCustomer, Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '' });
    setProducts([]);
    setPayments({
      splitPaymentEnabled: false,
      selectedPaymentMethod: 'Cash',
      cashPayment: '',
      walletProvider: 'PhonePe',
      walletAmount: '',
      walletTxNo: '',
      chequeAmount: '',
      chequeNo: '',
      couponNo: '',
      couponAmount: '0',
      couponAmountLocked: false,
      creditNoteNo: '',
      creditNoteAmount: '0',
      pointsRedeem: '',
      eligiblePoints: '',
      bankTransAmount: '',
      otherAmount: '',
      payBefore: '',
    });
    setNotes('');
    setRepName('');
    setInvoiceForm({
      QuoteNo: '',
      Mode: '',
      VehicleNo: '',
      Delivered: true,
      AddInFIFO: settings?.fifoInvoiceOrder ?? false,
    });
    setShipToForm({ Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
    setShipToSame(true);
    setIsUnknown(keepUnknown);
  };

  // Clear only customer details (and ship-to / unknown); leaves products, payments, notes, etc. Preserves State.
  const clearCustomerDetails = () => {
    const keepUnknown = settings?.unknownUserTicketed ?? false;
    setCustomer(prev => ({ ...initialCustomer, State: prev.State, Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '' }));
    setShipToForm(prev => ({ Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: prev.State }));
    setShipToSame(true);
    setIsUnknown(keepUnknown);
  };

  return (
    <SalesInvoiceContext.Provider value={{
      customer, setCustomer,
      products, setProducts,
      payments, setPayments,
      notes, setNotes,
      repName, setRepName,
      invoiceForm, setInvoiceForm,
      shipToForm, setShipToForm,
      shipToSame, setShipToSame,
      isUnknown, setIsUnknown,
      isEditMode, setIsEditMode,
      editingInvoiceId, setEditingInvoiceId,
      onBackToReports, setOnBackToReports,
      onUpdateSuccess, setOnUpdateSuccess,
      resetForNewInvoice,
      clearCustomerDetails,
    }}>
      {children}
    </SalesInvoiceContext.Provider>
  );
};

export const useSalesInvoice = () => useContext(SalesInvoiceContext); 